const PhotoGeoChecker = (() => {
    // ---------- Public API ----------
    async function hasGeo(file) {
        const buf = await file.arrayBuffer();
        const dv = new DataView(buf);

        if(isJPEG(dv)) {
            const exif = findExifInJPEG(dv);
            if(!exif) return false;
            return tiffHasGps(exif);

        } else if(isPNG(dv)) {
            const exif = findExifInPNG(dv);
            if(!exif) return false;
            return tiffHasGps(exif);

        } else if(isWebP(dv)) {
            const exif = findExifInWebP(dv);
            if(!exif) return false;
            return tiffHasGps(exif);

        } else if(isHEIF(dv)) {
            const exif = findExifInHEIF(dv);
            if(!exif) return false;
            return tiffHasGps(exif);
        }

        console.log('It is unknown format');
        return false;
    }

    // ---------- Signature checks ----------
    function isJPEG(v) {
        return v.byteLength >= 2 && v.getUint16(0, false) === 0xffd8;
    }
    function isPNG(v) {
        if(v.byteLength < 8) return false;
        const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
        for(let i = 0; i < 8; i++) if(v.getUint8(i) !== sig[i]) return false;
        return true;
    }
    function isWebP(v) {
        if(v.byteLength < 12) return false;
        return ascii(v, 0, 4) === "RIFF" && ascii(v, 8, 4) === "WEBP";
    }
    function isHEIF(v) {
        if(v.byteLength < 12) return false;
        const size = v.getUint32(0, false);
        const type = ascii(v, 4, 4);
        if(type !== "ftyp" || size < 12 || size > v.byteLength) return false;
        const major = ascii(v, 8, 4);
        const brands = new Set(["heic", "heix", "heif", "mif1", "hevc", "avis", "avif"]);
        return brands.has(major);
    }

    // ---------- ASCII helper ----------
    function ascii(v, offset, len) {
        let s = "";
        for(let i = 0; i < len; i++) {
            s += String.fromCharCode(v.getUint8(offset + i));
        }
        return s;
    }

    // ---------- JPEG: find APP1 Exif payload (TIFF starts after "Exif\0\0") ----------
    function findExifInJPEG(v) {
        let off = 2; // after SOI
        while(off + 4 <= v.byteLength) {
            const marker = v.getUint16(off, false); off += 2;
            if(marker === 0xffda) break; // SOS
            if(off + 2 > v.byteLength) break;
            const len = v.getUint16(off, false); off += 2;
            if(marker === 0xffe1 && len >= 8) {
                if(ascii(v, off, 4) === "Exif" && v.getUint16(off + 4, false) === 0x0000) {
                const tiffStart = off + 6;
                const tiffLen = len - 8;
                return new DataView(v.buffer, v.byteOffset + tiffStart, tiffLen);
                }
            }
            off += len - 2;
        }
        return null;
    }

    // ---------- PNG: find eXIf chunk (payload is raw TIFF/EXIF) ----------
    function findExifInPNG(v) {
        let off = 8; // skip PNG signature
        while(off + 12 <= v.byteLength) {
            const length = v.getUint32(off, false); off += 4;
            const type = ascii(v, off, 4); off += 4;
            if(type === "eXIf") {
                const tiffStart = off;
                const tiffLen = length;
                if(tiffStart + tiffLen <= v.byteLength) {
                return new DataView(v.buffer, v.byteOffset + tiffStart, tiffLen);
                } else {
                return null;
                }
            }
            off += length + 4; // data + CRC
        }
        return null;
    }

    // ---------- WebP (RIFF): find 'EXIF' chunk (payload is raw TIFF/EXIF) ----------
    function findExifInWebP(v) {
        let off = 12; // skip "RIFF" + size + "WEBP"
        while(off + 8 <= v.byteLength) {
            const fourcc = ascii(v, off, 4); off += 4;
            let size = v.getUint32(off, true); off += 4; // little-endian
            if(fourcc === "EXIF") {
                const start = off;
                const len = size;
                if(start + len <= v.byteLength) {
                return new DataView(v.buffer, v.byteOffset + start, len);
                } else {
                return null;
                }
            }
            off += size + (size % 2); // chunks padded to even sizes
        }
        return null;
    }

    // ---------- HEIF/HEIC (ISOBMFF): fetch Exif item data and return its TIFF payload ----------
    function findExifInHEIF(v) {
        function readBox(pos) {
            if(pos + 8 > v.byteLength) return null;
            let size = v.getUint32(pos, false);
            const type = ascii(v, pos + 4, 4);
            let header = 8;
            if(size === 1) {
                if(pos + 16 > v.byteLength) return null;
                const hi = v.getUint32(pos + 8, false);
                const lo = v.getUint32(pos + 12, false);
                size = hi * 2 ** 32 + lo;
                header = 16;
            }
            if(size < header || pos + size > v.byteLength) return null;
            return { type, start: pos, header, size, end: pos + size };
        }

        // Find 'meta' box
        let off = 0, metaStart = -1, metaEnd = -1, metaHeader = 0;
        while(off < v.byteLength) {
            const box = readBox(off);
            if(!box) break;
            if(box.type === "meta") { metaStart = box.start; metaEnd = box.end; metaHeader = box.header; break; }
            off = box.end;
        }
        if(metaStart < 0) return null;

        let p = metaStart + metaHeader + 4; // skip FullBox version/flags
        let exifItemId = null;
        let ilocPtr = -1, ilocHeader = 0;

        const isFourCC = (s) => s.length === 4 && /^[ -~]{4}$/.test(s);

        while(p < metaEnd) {
            const child = readBox(p);
            if(!child || child.end > metaEnd) break;

            if(child.type === "iinf") {
                // Minimal iinf parse: read entryCount (v0 uses 16-bit, others 32-bit), then scan 'infe' boxes
                let q = child.start + child.header;
                const iinfVersion = v.getUint8(q);
                q += 4;
                const entryCount = (iinfVersion === 0) ? v.getUint16(q, false) : v.getUint32(q, false);
                q += (iinfVersion === 0) ? 2 : 4;

                let r = q;
                for(let i = 0; i < entryCount; i++) {
                    const infe = readBox(r);
                    if(!infe || infe.type !== "infe") break;

                    // We only care about version ≥2 and item_type === "Exif"
                    let s = infe.start + infe.header;
                    const infeVersion = v.getUint8(s); s += 4; // skip version/flags

                    if(infeVersion >= 2) {
                        // Fast path: try 16-bit item_ID first (matches iPhone HEIC), fall back to 32-bit if needed.
                        const try16Type = (s + 2 + 2 + 4 <= infe.end) ? ascii(v, s + 2 + 2, 4) : "";
                        const use16 = isFourCC(try16Type);
                        const itemId = use16 ? v.getUint16(s, false) : v.getUint32(s, false);
                        s += use16 ? 2 : 4;

                        s += 2; // item_protection_index
                        const itemType = (s + 4 <= infe.end) ? ascii(v, s, 4) : ""; s += 4;

                        if(itemType === "Exif") {
                            exifItemId = itemId;
                            // We can stop scanning iinf as soon as we find Exif
                            break;
                        }
                    }
                    r = infe.end;
                }
            } else if(child.type === "iloc") {
                ilocPtr = child.start;
                ilocHeader = child.header;
            }
            if(exifItemId !== null && ilocPtr >= 0) break; // we have what we need
            p = child.end;
        }

        if(exifItemId === null || ilocPtr < 0) return null;

        // Parse 'iloc'
        let s = ilocPtr + ilocHeader; // base
        const ilocVersion = v.getUint8(s); s += 4; // skip version/flags
        const b1 = v.getUint8(s); s++;
        const b2 = v.getUint8(s); s++;
        const offsetSize = (b1 & 0xf0) >> 4;
        const lengthSize = (b1 & 0x0f);
        const baseOffsetSize = (b2 & 0xf0) >> 4;
        const indexSize = (b2 & 0x0f);

        let itemCount;
        if(ilocVersion < 2) { itemCount = v.getUint16(s, false); s += 2; }
        else { itemCount = v.getUint32(s, false); s += 4; }

        const readN = (pos, nbytes) => {
            let val = 0;
            for(let i = 0; i < nbytes; i++) val = (val << 8) | v.getUint8(pos + i);
            return val >>> 0;
        };

        let exifOffset = 0, exifLength = 0;
        for(let i = 0; i < itemCount; i++) {
            const itemId = ilocVersion < 2 ? v.getUint16(s, false) : v.getUint32(s, false);
            s += ilocVersion < 2 ? 2 : 4;

            if(ilocVersion === 1 || ilocVersion === 2) s += 2; // constructionMethod (ignore)
            s += 2; // dataReferenceIndex

            const baseOffset = readN(s, baseOffsetSize); s += baseOffsetSize;
            const extentCount = v.getUint16(s, false); s += 2;

            if(itemId === exifItemId) {
                if(extentCount < 1) return null;
                if(indexSize > 0) s += indexSize;
                const extentOffset = readN(s, offsetSize); s += offsetSize;
                const extentLength = readN(s, lengthSize); s += lengthSize;
                exifOffset = baseOffset + extentOffset;
                exifLength = extentLength;
                break;
            } else {
                for(let k = 0; k < extentCount; k++) {
                    if(indexSize > 0) s += indexSize;
                    s += offsetSize + lengthSize;
                }
            }
        }

            if(exifLength <= 8 || exifOffset + exifLength > v.byteLength) return null;

        // HEIF Exif item typically starts with 4 zero bytes before TIFF
        let lead = exifOffset;
        if(v.getUint32(lead, false) === 0) lead += 4;

        // Strip "Exif\0\0" if present so we return a pure TIFF payload
        if(ascii(v, lead, 4) === "Exif" && v.getUint16(lead + 4, false) === 0x0000) {
            lead += 6;
        }

        // If still not at TIFF ("II"/"MM"), scan a small window for a valid TIFF header
        const looksLikeTiff = (pos) => {
            if(pos + 8 > v.byteLength) return false;
            const a = ascii(v, pos, 2);
            if(a !== "II" && a !== "MM") return false;
            const little = (a === "II");
            return v.getUint16(pos + 2, little) === 0x2a;
        };
        if(!(ascii(v, lead, 2) === "II" || ascii(v, lead, 2) === "MM")) {
            const limit = Math.min(64, exifLength);
            let found = -1;
            for(let delta = 0; delta < limit; delta++) {
                if(looksLikeTiff(lead + delta)) { found = lead + delta; break; }
            }
            if(found >= 0) lead = found; else return null;
        }

        const len = exifOffset + exifLength - lead;
        if(len <= 8) return null;

        return new DataView(v.buffer, v.byteOffset + lead, len);
    }

    // ---------- TIFF/EXIF GPS detection ----------
    function tiffHasGps(tiff) {
        if (tiff.byteLength < 8) { console.log("Invalid TIFF: too small"); return false; }

        const ascii2 = (dv, o) => String.fromCharCode(dv.getUint8(o), dv.getUint8(o+1));
        const endianMark = ascii2(tiff, 0);
        const little = endianMark === "II";
        const get8  = (o) => tiff.getUint8(o);
        const get16 = (o) => tiff.getUint16(o, little);
        const get32 = (o) => tiff.getUint32(o, little);

        if (get16(2) !== 0x2A) { console.log("Invalid TIFF: missing 0x2A"); return false; }

        const ifd0 = get32(4);
        if (ifd0 <= 0 || ifd0 + 2 > tiff.byteLength) { console.log("Bad IFD0"); return false; }

        const entries = get16(ifd0);
        const base = ifd0 + 2;
        let gpsPtr = 0;
        for (let i = 0; i < entries; i++) {
            const e = base + i * 12;
            if (e + 12 > tiff.byteLength) break;
            const tag = get16(e);
            if (tag === 0x8825) {
                gpsPtr = get32(e + 8);
                break;
            }
        }
        if (!gpsPtr) { console.log("No GPS IFD pointer"); return false; }

        const getRational = (o) => {
            if (o + 8 > tiff.byteLength) return NaN;
            const num = get32(o);
            const den = get32(o + 4);
            if (den === 0) return NaN;
            return num / den;
        };

        const readAscii = (pos, count) => {
            const n = Math.max(0, Math.min(count, tiff.byteLength - pos));
            let s = "";
            for (let i = 0; i < n; i++) {
                const c = get8(pos + i);
                if (c === 0) break;
                s += String.fromCharCode(c);
            }
            return s;
        };

        const dmsToDecimal = (d, m, s, ref) => {
            if (![d, m, s].every((v) => Number.isFinite(v))) return NaN;
            let val = d + m / 60 + s / 3600;
            if (ref === "S" || ref === "W") val *= -1;
            return val;
        };

        if (gpsPtr + 2 > tiff.byteLength) { console.log("Bad GPS IFD"); return false; }
        const gCount = get16(gpsPtr);
        const gBase = gpsPtr + 2;

        let latDMS = null, lonDMS = null;
        let latRef = null, lonRef = null;

        for (let i = 0; i < gCount; i++) {
            const e = gBase + i * 12;
            if (e + 12 > tiff.byteLength) break;

            const tag   = get16(e);
            const type  = get16(e + 2);
            const count = get32(e + 4);
            const valOrOff = get32(e + 8);

            if (tag === 0x0001 && type === 2 && count >= 1) {
                if (count <= 4) {
                    latRef = String.fromCharCode(get8(e + 8)).toUpperCase();
                } else {
                    latRef = readAscii(valOrOff, count).toUpperCase();
                }
            }

            if (tag === 0x0003 && type === 2 && count >= 1) {
                if (count <= 4) {
                    lonRef = String.fromCharCode(get8(e + 8)).toUpperCase();
                } else {
                    lonRef = readAscii(valOrOff, count).toUpperCase();
                }
            }

            if (tag === 0x0002 && type === 5 && count === 3) {
                const p = valOrOff;
                if (p + 24 <= tiff.byteLength) {
                    latDMS = [ getRational(p), getRational(p + 8), getRational(p + 16) ];
                }
            }

            if (tag === 0x0004 && type === 5 && count === 3) {
                const p = valOrOff;
                if (p + 24 <= tiff.byteLength) {
                    lonDMS = [ getRational(p), getRational(p + 8), getRational(p + 16) ];
                }
            }
        }

        const hasAll = latDMS && lonDMS && latRef && lonRef;
        console.log("hasLat", !!latDMS);
        console.log("hasLon", !!lonDMS);
        console.log("latRefOk", !!latRef);
        console.log("lonRefOk", !!lonRef);

        if(!hasAll) {
            console.log("GPS data not found or incomplete.");
            return false;
        }

        const lat = dmsToDecimal(latDMS[0], latDMS[1], latDMS[2], latRef);
        const lon = dmsToDecimal(lonDMS[0], lonDMS[1], lonDMS[2], lonRef);

        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            console.log("GPS values present, but could not compute decimal coordinates.");
            return false;
        }

        console.log(`Latitude:  ${lat} (${latDMS[0]}° ${latDMS[1]}' ${latDMS[2]}" ${latRef})`);
        console.log(`Longitude: ${lon} (${lonDMS[0]}° ${lonDMS[1]}' ${lonDMS[2]}" ${lonRef})`);
        return { latitude: lat, longitude: lon, latRef, lonRef, latDMS, lonDMS };
    }

    return { hasGeo };
})();

export default PhotoGeoChecker;